import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, Interpolation } from '@emotion/react';
import { TaskStatus, TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import dayjs from 'dayjs';
import { InfiniteData, QueryClient, QueryKey, useQueryClient } from 'react-query';
import { AnalyticsCommonTypes, CallIntelligenceApi } from '@frontend/api-analytics';
import { CallIntelMutations, CallIntelQueries, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { AdaptoActions } from '@frontend/adapto-actions';
import { LocationChip, UserCard } from '@frontend/analytics-shared';
import { getUser } from '@frontend/auth-helpers';
import { PanelEvents, useCallInsightController } from '@frontend/call-insight-panel';
import {
  CallIntelChip,
  callIntelUtils,
  generateTrackingId,
  TaskStatusSwitcher,
  useCallIntelContactTypesLabel,
  useCallIntelDefaultsShallowStore,
  CI_DATE_FORMATS,
} from '@frontend/call-intel-core';
import { Actions as ContactActions } from '@frontend/contact-actions';
import { DemoIdUtils } from '@frontend/demo-interceptors';
import { PrivateCommsBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { useScopedInfiniteQuery, useScopedQuery } from '@frontend/scope';
import { TaskEvents, useDeleteTask, useTaskCitation } from '@frontend/task-center-scope';
import { theme } from '@frontend/theme';
import {
  Accordion,
  Button,
  styles as designSystemStyles,
  emptyStateGraphics,
  IconButton,
  Pagination,
  PopoverMenu,
  PopoverMenuItem,
  SkeletonLoaders,
  Text,
  useAlert,
  usePopoverMenu,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { trackingIds } from '../../tracking-ids';
import { generateContactId } from '../../utils/call-intelligence';
import { MIN_SEARCH_LENGTH } from './filters/constants';
import { SearchFilter } from './filters/molecules/search-filter';
import { CallIntelSortFilter } from './filters/molecules/sort-filter';
import { useActiveFilters } from './hooks/use-active-filters';
import { useCallIntelDemoFlags } from './hooks/use-call-intel-demo-flags';
import { useCallIntelFollowUpsShallowStore } from './hooks/use-call-intel-follow-ups-store';
import { useCallIntelLocations } from './hooks/use-call-intel-locations';
import { OfficeUserName } from './office-user-name';

type CallInfoProps = {
  call: CallIntelTypes.CallRecord;
  isMultiLocation: boolean;
  isSmallMode: boolean;
  locations: AnalyticsCommonTypes.StringRecord;
};

type ContactActionsGroupProps = {
  customStyles?: Interpolation;
  followUpItem: CallIntelTypes.FollowUpItem;
};

type EmptyStateProps = {
  label: string;
};

type FollowUpCardProps = {
  drillDownTaskType: TaskType;
  followUpItem: CallIntelTypes.FollowUpItem;
  isDrillDown: boolean;
  isSmallMode: boolean;
  shouldLoadTasks: boolean;
  taskStatusFilters: TaskStatus[];
  value: string;
};

type FollowUpLoaderProps = {
  count?: number;
  disabled?: boolean;
  variant: 'followUp' | 'task' | 'taskMobile' | 'stats';
};

type FollowUpStatsProps = {
  isSmallMode: boolean;
  taskStats: CallIntelTypes.TaskStats;
  taskStatusFilters: TaskStatus[];
  totalPatients: number;
};

type Props = {
  drillDownTaskType: TaskType;
  isDrillDown: boolean;
  searchQuery: CallIntelTypes.SearchState;
  sortOption: CallIntelTypes.CallIntelSortOption;
  onSearchQueryChange: (value: string, debounced?: boolean) => void;
  onSortOptionChange: (option: CallIntelTypes.CallIntelSortOption) => void;
};

type StatusChangeParams = {
  callId: string;
  taskId: string;
  newStatus: TaskStatus;
  oldStatus: TaskStatus;
};

type TaskMeatballMenuProps = {
  disabled?: boolean;
  isDemoAccount?: boolean;
  onDeleteTask?: () => void;
  onViewCallTakeaways?: () => void;
  onViewTask?: () => void;
};

// Constants
const DEFAULT_CALL_LIMIT = 5;
const MAX_EXPECTED_TASKS = 3;
const DEFAULT_PRELOAD_COUNT = 1; // Number of follow-up contacts beyond the currently visible item to preload with tasks
const TOTAL_SELECTABLE_TASK_STATUSES = 3; // This is count of the task types which are to-do , in-progress , completed

const processFollowUpsData = (data: InfiniteData<CallIntelTypes.ListFollowupsResponse>) => {
  const allFollowUps: CallIntelTypes.FollowUpItem[] = [];

  let totalFollowUps = 0;

  const currentPage = data.pages[0];
  if (!currentPage) return { allFollowUps, totalFollowUps };

  totalFollowUps = currentPage.totalFollowUps ?? 0;

  (currentPage.followUps ?? []).forEach((followUp) => {
    const calls = followUp.calls ?? [];
    if (!calls.length) return;

    const followUpItem = {
      contact: { ...calls[0], contactId: generateContactId(calls[0]) },
      allCalls: calls,
      callTaskMapping: {},
      totalCalls: followUp.totalCalls ?? 0,
      totalTasks: followUp.totalTasks ?? 0,
    };

    allFollowUps.push(followUpItem);
  });

  return { allFollowUps, totalFollowUps };
};

const getListTaskQueryKey = ({
  baseKey,
  locationIds,
  sourceIds,
}: {
  baseKey: QueryKey;
  locationIds: string[];
  sourceIds: string[];
}) => {
  const [ns, endpoint, filterReq] = baseKey as [string, string, Record<string, any>];
  const newReq = {
    ...filterReq,
    filter: {
      ...filterReq.filter,
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_SOURCE_ID]: { sourceId: sourceIds },
      [CallIntelTypes.TaskFilterTypeEnum.FILTER_BY_LOCATION]: { locationId: locationIds },
    },
  };

  return [ns, endpoint, newReq];
};

const invalidateTaskListQuery = ({
  queryClient,
  listTaskQueryKey,
  selectedCall,
}: {
  queryClient: QueryClient;
  listTaskQueryKey: QueryKey;
  selectedCall: CallIntelTypes.CallRecord | null;
}) => {
  const queryKeyToInvalidate = getListTaskQueryKey({
    baseKey: listTaskQueryKey,
    locationIds: [selectedCall?.locationId ?? ''],
    sourceIds: [selectedCall?.id ?? ''],
  });

  queryClient.invalidateQueries(queryKeyToInvalidate, { exact: true });
};

const CallInfo = ({ call, isMultiLocation, isSmallMode, locations }: CallInfoProps) => {
  const { t } = useTranslation('analytics');

  const formattedTimestamp = useMemo(
    () => dayjs(call.startTime).format(CI_DATE_FORMATS.FULL_TIMESTAMP),
    [call.startTime]
  );

  const officeUserConfig = useMemo(
    () => ({
      ...call.officeUser,
      isDataUnavailable: callIntelUtils.isOfficeUserDataUnavailable(call.startTime),
      isInactive: callIntelUtils.isUserInactive(call.officeUser),
    }),
    [call.officeUser, call.startTime]
  );

  if (isSmallMode) {
    return (
      <div css={styles.callWrapper}>
        <Text size='medium'>{formattedTimestamp}</Text>
        <span> • </span>
        <Text size='medium'>
          <OfficeUserName {...officeUserConfig} css={styles.officeUserNameText} />
        </Text>
      </div>
    );
  }

  return (
    <div css={styles.callWrapper}>
      <div className='office-user-wrapper'>
        <Text color='subdued' size='medium'>
          {t('Office User')}
        </Text>
        <Text size='medium'>
          <OfficeUserName {...officeUserConfig} css={styles.officeUserNameText} />
        </Text>
      </div>
      <div className='time-stamp-wrapper'>
        <Text color='subdued' size='medium'>
          {t('Time of Call')}
        </Text>
        <Text size='medium'>{formattedTimestamp}</Text>
      </div>
      {isMultiLocation && (
        <div className='location-wrapper'>
          <Text size='medium' color='subdued'>
            {t('Location')}
          </Text>
          <LocationChip
            locationName={call.locationId ? locations[call.locationId] || call.locationId : ''}
            maxWidth={300}
          />
        </div>
      )}
    </div>
  );
};

const ContactActionsGroup = ({ followUpItem, customStyles }: ContactActionsGroupProps) => {
  const { t } = useTranslation('analytics');

  return (
    <AdaptoActions
      enableCloseEvent
      iconColor='default'
      variant='action-buttons'
      css={[styles.actionsWrapper, customStyles]}
    >
      <ContactActions.Call
        context={{
          personId: followUpItem.contact.person?.id,
          phoneNumber: followUpItem.contact.phoneNumber?.nationalNumber?.toString(),
        }}
        label={t('Call')}
        trackingIdSuffix='follow-up-contact-actions'
      />
      <ContactActions.Message
        context={{
          personId: followUpItem.contact.person?.id,
          personPhone: followUpItem.contact.phoneNumber?.nationalNumber?.toString(),
          groupIds: Array.from(new Set(followUpItem.allCalls.map((call) => call.locationId ?? ''))),
        }}
        label={t('Message')}
        trackingIdSuffix='follow-up-contact-actions'
      />
    </AdaptoActions>
  );
};

// Loader component for follow-ups
const FollowUpLoader = ({ count = 1, disabled = false, variant = 'followUp' }: FollowUpLoaderProps) => {
  if (variant === 'stats') {
    return <SkeletonLoaders.Loader height='120px' />;
  }

  if (variant === 'taskMobile') {
    return (
      <>
        {Array.from({ length: count }).map((_, idx) => (
          <div key={idx} css={styles.taskMobileLoadingWrapper}>
            <SkeletonLoaders.Loader height='24px' width='70%' />
            <SkeletonLoaders.Loader height='24px' width='30%' />
          </div>
        ))}
      </>
    );
  }

  if (variant === 'task') {
    return (
      <>
        {Array.from({ length: count }).map((_, idx) => (
          <div key={idx} css={styles.taskRow}>
            <div className='data-cols'>
              <SkeletonLoaders.Loader height='12px' />
            </div>

            <div css={styles.taskActionWrapper}>
              <div className='status-column'>
                <SkeletonLoaders.Loader height='12px' />
              </div>

              <div className='reason-column'>
                <SkeletonLoaders.Loader height='12px' />
              </div>
            </div>
            <div className='action-column'>
              <TaskMeatballMenu disabled={disabled} />
            </div>
          </div>
        ))}
      </>
    );
  }
  return (
    <div css={styles.followUpLoaderWrapper}>
      {Array.from({ length: count }).map((_, idx) => (
        <SkeletonLoaders.Loader className='row' key={idx} height={theme.spacing(10.5)} />
      ))}
    </div>
  );
};

// Empty state component for follow-ups
const EmptyState = ({ label }: EmptyStateProps) => {
  const EmptyStateGraphicNoFollowUps = emptyStateGraphics.noCallFollowUps;

  return (
    <div css={styles.emptyStateWrapper}>
      <EmptyStateGraphicNoFollowUps height={300} width={300} />
      <Text css={styles.emptyStateText}>{label}</Text>
    </div>
  );
};

// Stats component for follow-ups
const FollowUpStats = ({ isSmallMode, taskStats, taskStatusFilters, totalPatients }: FollowUpStatsProps) => {
  const { t } = useTranslation('analytics');
  const { patientCapitalizedPlural: patientCapitalizedPluralToken } = useTranslationTokens();

  const { totalTasks, taskCompletionRate, shouldDisplayAllStatuses } = useMemo(() => {
    const computedTotalTasks =
      (taskStats?.STATUS_TODO ?? 0) + (taskStats?.STATUS_IN_PROGRESS ?? 0) + (taskStats?.STATUS_COMPLETED ?? 0);

    return {
      totalTasks: computedTotalTasks,
      taskCompletionRate: computedTotalTasks
        ? `${Math.round(((taskStats?.STATUS_COMPLETED ?? 0) / computedTotalTasks) * 100)}%`
        : '0%',
      shouldDisplayAllStatuses:
        taskStatusFilters.length === 0 || taskStatusFilters.length === TOTAL_SELECTABLE_TASK_STATUSES,
    };
  }, [taskStats, taskStatusFilters]);

  const taskStatusLabels = useMemo(
    () => ({
      [TaskStatus.STATUS_TODO]: t('To-Do'),
      [TaskStatus.STATUS_IN_PROGRESS]: t('In Progress'),
      [TaskStatus.STATUS_COMPLETED]: t('Completed'),
    }),
    [t]
  );

  const getTaskStatusStats = useCallback(() => {
    const stats: Array<{ label: string; value: string }> = [];

    Object.entries(taskStatusLabels).forEach(([status, label]) => {
      if (taskStatusFilters.includes(status as TaskStatus) || shouldDisplayAllStatuses) {
        stats.push({ label, value: `${taskStats[status as TaskStatus] ?? 0}` });
      }
    });

    return stats;
  }, [shouldDisplayAllStatuses, taskStats, taskStatusFilters, taskStatusLabels]);

  const visibleStats = useMemo(() => {
    const statsList: Array<{ label: string; value: string }> = [];

    if (isSmallMode) {
      return getTaskStatusStats();
    }

    statsList.push({ label: patientCapitalizedPluralToken, value: `${totalPatients}` });

    if (shouldDisplayAllStatuses) {
      statsList.push({ label: t('Total Tasks'), value: `${totalTasks}` });
    }

    statsList.push(...getTaskStatusStats());

    if (shouldDisplayAllStatuses) {
      statsList.push({ label: t('Task Completion Rate'), value: taskCompletionRate });
    }

    return statsList;
  }, [
    isSmallMode,
    patientCapitalizedPluralToken,
    totalPatients,
    shouldDisplayAllStatuses,
    getTaskStatusStats,
    t,
    totalTasks,
    taskCompletionRate,
  ]);

  return (
    <div css={styles.statsWrapper} data-testid='follow-up-stats'>
      {visibleStats.map(({ label, value }) => (
        <div className='info-wrapper' key={label}>
          <Text color='subdued'>{label}</Text>
          <Text className='value' weight='bold'>
            {value}
          </Text>
        </div>
      ))}
    </div>
  );
};

const TaskMeatballMenu = ({
  disabled = false,
  isDemoAccount,
  onViewTask,
  onViewCallTakeaways,
  onDeleteTask,
}: TaskMeatballMenuProps) => {
  const { t } = useTranslation('analytics');

  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement: 'bottom-end' });

  return (
    <div>
      <IconButton
        {...getTriggerProps()}
        label={t('Open Menu')}
        css={styles.meatBallBtnWrapper}
        trackingId={generateTrackingId({
          component: 'follow-up-page',
          context: 'meatball-menu-click',
          isDemoAccount,
        })}
      >
        <Icon name='more' />
      </IconButton>

      {!disabled && (
        <PopoverMenu {...getMenuProps()}>
          {onViewTask && (
            <PopoverMenuItem
              {...getItemProps({
                index: 0,
                onClick: onViewTask,
              })}
              Icon={() => <Icon name='expand-small' />}
              trackingId={generateTrackingId({
                component: 'follow-up-page',
                context: 'meatball-menu-task-btn-click',
                isDemoAccount,
              })}
            >
              <Text as='span'>{t('View Task')}</Text>
            </PopoverMenuItem>
          )}

          {onViewCallTakeaways && (
            <PopoverMenuItem
              {...getItemProps({
                index: 1,
                onClick: onViewCallTakeaways,
              })}
              Icon={() => <Icon name='document-filled-small' />}
              trackingId={generateTrackingId({
                component: 'follow-up-page',
                context: 'meatball-menu-takeaway-btn-click',
                isDemoAccount,
              })}
            >
              <Text as='span'>{t('View Call Insights')}</Text>
            </PopoverMenuItem>
          )}

          {onDeleteTask && (
            <PopoverMenuItem
              {...getItemProps({
                index: 2,
                onClick: onDeleteTask,
              })}
              Icon={() => (
                <Icon
                  name='trash-small'
                  css={css`
                    color: ${theme.colors.critical50};
                  `}
                />
              )}
              trackingId={generateTrackingId({
                component: 'follow-up-page',
                context: 'meatball-menu-delete-btn-click',
                isDemoAccount,
              })}
            >
              <Text
                as='span'
                css={css`
                  color: ${theme.colors.critical50};
                `}
              >
                {t('Delete Task')}
              </Text>
            </PopoverMenuItem>
          )}
        </PopoverMenu>
      )}
    </div>
  );
};

const FollowUpCard = forwardRef<HTMLDivElement, FollowUpCardProps>(
  (
    {
      drillDownTaskType,
      followUpItem,
      isDrillDown,
      isSmallMode,
      shouldLoadTasks,
      taskStatusFilters,
      value,
    }: FollowUpCardProps,
    ref
  ) => {
    const { t } = useTranslation('analytics');
    const alert = useAlert();
    const queryClient = useQueryClient();
    const user = getUser();

    const itemRef = useRef<HTMLDivElement | null>(null);

    const { isDemoAccount, isDemoModal } = useCallIntelDemoFlags();
    const contactTypeLabelMap = useCallIntelContactTypesLabel();
    const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');
    const { filters } = useActiveFilters();
    const { updateTaskStatus, updateCallTaskMapping } = useCallIntelFollowUpsShallowStore(
      'updateTaskStatus',
      'updateCallTaskMapping'
    );

    const {
      activeCall,
      onEvent,
      offEvent,
      open: openCallInsightPanel,
    } = useCallInsightController({
      source: 'callIntel',
    });

    const { openTaskWithTaskId, onTaskPanelEvent, offTaskPanelEvent } = useTaskCitation();
    const { locations, isMultiLocation } = useCallIntelLocations();

    const selectedCall = activeCall?.callIntelInsights as CallIntelTypes.CallRecord | null;

    // Manage how many calls are shown
    const [callLimit, setCallLimit] = useState(DEFAULT_CALL_LIMIT);
    const [extraCalls, setExtraCalls] = useState<CallIntelTypes.CallRecord[]>([]);

    const [selectedTaskConfig, setSelectedTaskConfig] = useState<{ callId: string; taskId: string } | null>(null);

    const {
      ConfirmationModal,
      modalProps: confirmationModalProps,
      triggerProps,
    } = useDeleteTask({ id: selectedTaskConfig?.taskId });

    // Calculate displayed calls
    const displayedCalls = useMemo(() => {
      return followUpItem.allCalls.slice(0, callLimit);
    }, [followUpItem.allCalls, callLimit]);

    // The initial calls to fetch tasks for
    const initialCalls = useMemo(() => {
      return followUpItem.allCalls.slice(0, DEFAULT_CALL_LIMIT);
    }, [followUpItem.allCalls]);
    const initialCallIds = useMemo(
      () => initialCalls.map((c) => c.id).filter((id): id is string => !!id),
      [initialCalls]
    );

    // A couple of derived values
    const totalCalls = followUpItem.allCalls.length;
    const canLoadMore = callLimit < totalCalls;
    const hasMoreThanOnePage = totalCalls > DEFAULT_CALL_LIMIT;
    const totalTasks = followUpItem.totalTasks;

    // Fetch tasks query for the initial calls
    const {
      data: taskDataRes,
      isFetching: isTaskLoading,
      queryKey: listTaskQueryKey,
    } = CallIntelQueries.useListTasks({
      payload: {
        callIds: initialCallIds,
        locations: filters.locations || [],
        includeMetadata: true,
        taskTypes: !!drillDownTaskType ? [drillDownTaskType] : filters.taskTypes ?? [],
        taskStatusFilters,
        isDemo: isDemoAccount,
        demoMetadata: isDemoAccount
          ? {
              callRecordList: initialCalls,
            }
          : undefined,
      },
      realQueryOptions: {
        enabled: shouldLoadTasks,
        staleTime: 0,
        cacheTime: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        onError: () => {
          alert.error(t('Failed to fetch tasks'));
        },
      },
    });

    // Fetch extra tasks fetch (when the user clicks "Load More Calls")
    const { isFetching: isExtraTasksLoading } = CallIntelQueries.useListTasks({
      payload: {
        callIds: extraCalls.map(({ id }) => id).filter((id): id is string => !!id),
        locations: filters.locations || [],
        includeMetadata: true,
        taskTypes: !!drillDownTaskType ? [drillDownTaskType] : filters.taskTypes ?? [],
        taskStatusFilters,
        isDemo: isDemoAccount,
        demoMetadata: {
          callRecordList: [],
        },
      },
      realQueryOptions: {
        enabled: extraCalls.length > 0,
        staleTime: 0,
        cacheTime: 0,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        onSuccess: (res) => {
          const tasksByCallId: Record<string, CallIntelTypes.Task[]> = {};

          if (isDemoAccount) {
            // TODO: Remove this logic once BE demo data is updated
            // For demo data, distribute tasks based on tasksCount in each call
            let taskIndex = 0;
            extraCalls.forEach(({ id: callId, taskCount = 0 }) => {
              if (!callId) {
                return;
              }
              tasksByCallId[callId] =
                res.tasks?.slice(taskIndex, taskIndex + taskCount).map((task) => ({
                  ...task,
                  sourceId: callId,
                })) || [];
              taskIndex = Math.min(taskIndex + taskCount, res.tasks?.length || 0);
            });
          } else {
            // For real data, map tasks by sourceId as before
            res.tasks?.forEach((task) => {
              const callId = task.sourceId;

              if (!callId) return;

              if (!tasksByCallId[callId]) {
                tasksByCallId[callId] = [];
              }
              tasksByCallId[callId].push(task);
            });
          }

          const updatedCallTaskMapping = { ...followUpItem.callTaskMapping };

          Object.keys(tasksByCallId).forEach((callId) => {
            updatedCallTaskMapping[callId] = tasksByCallId[callId];
          });

          updateCallTaskMapping({
            followUpId: followUpItem.contact.contactId,
            callTaskMapping: updatedCallTaskMapping,
            calculate: false,
            shouldCleanUp: false,
            ...(drillDownTaskType && { taskType: drillDownTaskType }),
          });

          setExtraCalls([]);
        },
        onError: () => {
          alert.error(t('Failed to fetch tasks'));
        },
      },
    });

    const { mutate: updateTask } = CallIntelMutations.useUpdateTask({
      onSuccess: (_, taskPayload) => {
        const { taskType, callId, oldStatus, status } = taskPayload;
        if (taskType && callId && oldStatus && status) {
          const shouldUpdateTaskStatus = !isDrillDown || taskType === drillDownTaskType;
          if (shouldUpdateTaskStatus) {
            updateTaskStatus({
              callId,
              followUpId: followUpItem.contact.contactId,
              newStatus: status,
              oldStatus,
              taskId: taskPayload.id,
            });
          }

          invalidateTaskListQuery({ queryClient, listTaskQueryKey, selectedCall });
        }

        alert.success(t('Task updated successfully'));
      },
      onError: () => {
        alert.error(t('Unable to update task. Please try again.'));
      },
      isDemoAccount,
    });

    // If tasks came back for the first time, store them in callTaskMapping
    useEffect(() => {
      if (!Object.keys(followUpItem.callTaskMapping).length && taskDataRes?.tasks) {
        const tasksByCallId: Record<string, CallIntelTypes.Task[]> = {};

        if (isDemoAccount) {
          // TODO: Remove this logic once BE demo data is updated
          // For demo data, distribute tasks based on tasksCount in each call
          let taskIndex = 0;
          initialCalls.forEach(({ id: callId, taskCount = 0 }) => {
            if (!callId) {
              return;
            }
            tasksByCallId[callId] =
              taskDataRes.tasks?.slice(taskIndex, taskIndex + taskCount).map((task) => ({
                ...task,
                sourceId: callId,
              })) || [];
            taskIndex = Math.min(taskIndex + taskCount, taskDataRes.tasks?.length || 0);
          });
        } else {
          taskDataRes.tasks.forEach((task) => {
            const callId = task.sourceId;

            if (callId) {
              if (!tasksByCallId[callId]) {
                tasksByCallId[callId] = [];
              }
              tasksByCallId[callId].push(task);
            }
          });
        }

        const updatedCallTaskMapping = { ...followUpItem.callTaskMapping };

        Object.keys(tasksByCallId).forEach((callId) => {
          updatedCallTaskMapping[callId] = tasksByCallId[callId];
        });

        updateCallTaskMapping({
          followUpId: followUpItem.contact.contactId,
          callTaskMapping: updatedCallTaskMapping,
          calculate: isDemoAccount ? true : false,
          ...(drillDownTaskType && !!isDemoAccount && { taskType: drillDownTaskType }),
        });
      }
    }, [isDemoAccount, followUpItem?.callTaskMapping, taskDataRes?.tasks]);

    const handlePanelTasksUpdated = useCallback(
      ({ call, tasks }: PanelEvents['tasksUpdated']) => {
        if (call?.id && selectedCall && call.id === selectedCall.id) {
          const isCallInFollowUp = followUpItem.callTaskMapping[call.id];

          if (isCallInFollowUp) {
            updateCallTaskMapping({
              followUpId: followUpItem.contact.contactId,
              callTaskMapping: {
                [call.id]: tasks as CallIntelTypes.Task[],
              },
              calculate: false,
              shouldCleanUp: true,
              ...(drillDownTaskType && { taskType: drillDownTaskType }),
            });
          }
        }
      },
      [
        drillDownTaskType,
        followUpItem.callTaskMapping,
        followUpItem.contact.contactId,
        selectedCall,
        updateCallTaskMapping,
      ]
    );

    const handlePanelTasksDeleted = useCallback(
      ({ call, taskId }: PanelEvents['taskDeleted']) => {
        if (call?.id && selectedCall && call.id === selectedCall.id) {
          const isCallInFollowUp = followUpItem.callTaskMapping[call.id];

          if (isCallInFollowUp) {
            const tasks = followUpItem.callTaskMapping[call.id]?.filter((task) => task.id !== taskId);

            updateCallTaskMapping({
              followUpId: followUpItem.contact.contactId,
              callTaskMapping: {
                [call.id]: tasks as CallIntelTypes.Task[],
              },
              calculate: false,
              shouldCleanUp: true,
              ...(drillDownTaskType && { taskType: drillDownTaskType }),
            });
          }
        }
      },
      [
        drillDownTaskType,
        followUpItem.callTaskMapping,
        followUpItem.contact.contactId,
        selectedCall,
        updateCallTaskMapping,
      ]
    );

    const handleTaskPanelTaskUpdated = useCallback(
      ({ id: taskId, status }: TaskEvents['TASK_UPDATED']) => {
        if (taskId === selectedTaskConfig?.taskId) {
          if (DemoIdUtils.checkIsDemoId(taskId)) {
            taskId = DemoIdUtils.removeDemoDataFromId(taskId);
          }
          const callId = selectedTaskConfig.callId;
          const isCallInFollowUp = followUpItem.callTaskMapping[callId];
          if (isCallInFollowUp) {
            const tasks = followUpItem.callTaskMapping[callId]?.map((task) =>
              task.id === taskId ? { ...task, status } : task
            );
            updateCallTaskMapping({
              followUpId: followUpItem.contact.contactId,
              callTaskMapping: {
                [callId]: tasks as CallIntelTypes.Task[],
              },
              calculate: false,
              shouldCleanUp: true,
              ...(drillDownTaskType && { taskType: drillDownTaskType }),
            });

            invalidateTaskListQuery({ queryClient, listTaskQueryKey, selectedCall });
          }
        }
      },
      [
        drillDownTaskType,
        followUpItem.callTaskMapping,
        followUpItem.contact.contactId,
        listTaskQueryKey,
        queryClient,
        selectedCall,
        selectedTaskConfig?.callId,
        selectedTaskConfig?.taskId,
        updateCallTaskMapping,
      ]
    );

    const handleTaskPanelTaskDeleted = useCallback(
      ({ id: taskId }: TaskEvents['TASK_DELETED']) => {
        if (taskId === selectedTaskConfig?.taskId) {
          if (DemoIdUtils.checkIsDemoId(taskId)) {
            taskId = DemoIdUtils.removeDemoDataFromId(taskId);
          }
          const callId = selectedTaskConfig.callId;
          const isCallInFollowUp = followUpItem.callTaskMapping[callId];
          if (isCallInFollowUp) {
            const tasks = followUpItem.callTaskMapping[callId]?.filter((task) => task.id !== taskId);
            updateCallTaskMapping({
              followUpId: followUpItem.contact.contactId,
              callTaskMapping: {
                [callId]: tasks as CallIntelTypes.Task[],
              },
              calculate: false,
              shouldCleanUp: true,
              ...(drillDownTaskType && { taskType: drillDownTaskType }),
            });
            invalidateTaskListQuery({ queryClient, listTaskQueryKey, selectedCall });
          }
        }
      },
      [
        drillDownTaskType,
        followUpItem.callTaskMapping,
        followUpItem.contact.contactId,
        selectedTaskConfig?.callId,
        selectedTaskConfig?.taskId,
        listTaskQueryKey,
        selectedCall,
        updateCallTaskMapping,
        queryClient,
      ]
    );

    useEffect(() => {
      onEvent('tasksUpdated', handlePanelTasksUpdated);
      onEvent('taskDeleted', handlePanelTasksDeleted);
      return () => {
        offEvent('tasksUpdated', handlePanelTasksUpdated);
        offEvent('taskDeleted', handlePanelTasksDeleted);
      };
    }, [onEvent, offEvent, handlePanelTasksUpdated, handlePanelTasksDeleted]);

    useEffect(() => {
      onTaskPanelEvent('TASK_UPDATED', handleTaskPanelTaskUpdated);
      onTaskPanelEvent('TASK_DELETED', handleTaskPanelTaskDeleted);

      return () => {
        offTaskPanelEvent('TASK_UPDATED', handleTaskPanelTaskUpdated);
        offTaskPanelEvent('TASK_DELETED', handleTaskPanelTaskDeleted);
      };
    }, [handleTaskPanelTaskUpdated, handleTaskPanelTaskDeleted, onTaskPanelEvent, offTaskPanelEvent]);

    const handleDeleteTaskConfirmation = useCallback(
      ({ callId, taskId }: { callId: string; taskId: string }) => {
        setSelectedTaskConfig({ callId, taskId: isDemoAccount ? `demo-${taskId}` : taskId });
        triggerProps.onClick();
      },
      [triggerProps, isDemoAccount]
    );

    const handleStatusChange = useCallback(
      ({ callId, taskId, newStatus, oldStatus }: StatusChangeParams) => {
        const tasks = followUpItem.callTaskMapping[callId] || [];
        const oldTask = tasks.find((task) => task.id === taskId);

        if (oldTask) {
          if (taskId && user?.userID) {
            updateTask({
              id: taskId,
              status: newStatus,
              oldStatus,
              callId,
              taskType: oldTask.type,
              updatedBy: user.userID,
            });
          }
        }
      },
      [followUpItem, updateTask, user?.userID]
    );

    const handleOpenTaskPanel = useCallback(
      ({ callId, taskId }: { callId: string; taskId: string }) => {
        setSelectedTaskConfig({ callId, taskId: isDemoAccount ? `demo-${taskId}` : taskId });
        openTaskWithTaskId(isDemoAccount ? `demo-${taskId}` : taskId, !!isDemoAccount);
      },
      [openTaskWithTaskId, isDemoAccount]
    );

    const handleOpenCallTakeaways = useCallback(
      (call: CallIntelTypes.CallRecord) => {
        if (call.id !== selectedCall?.id) {
          openCallInsightPanel(
            isDemoAccount
              ? {
                  channelId: call?.id ?? '',
                  includeCallIntelligence: false,
                  // Call is passed in demo mode to sync the call insight state
                  callIntelData: call,
                  locationId: call?.locationId ?? '',
                }
              : {
                  channelId: call?.id ?? '',
                  includeCallIntelligence: true,
                  locationId: call?.locationId ?? '',
                },
            {
              defaultAccordion: 'callIntel',
              suppressGlobalPanelOpen: !!isDemoModal,
              trackingContext: {
                source: 'follow-up-page',
              },
            }
          );
        }
      },
      [isDemoModal, openCallInsightPanel, selectedCall?.id, isDemoAccount]
    );

    const handleLoadMore = useCallback(() => {
      const oldLimit = callLimit;
      const newLimit = oldLimit + 5;
      setCallLimit(newLimit);

      const newlyRevealedCalls = followUpItem.allCalls.slice(oldLimit, newLimit);
      setExtraCalls(newlyRevealedCalls);
    }, [callLimit, followUpItem]);

    const isLoadingTasks = isTaskLoading || isExtraTasksLoading;

    return (
      <Accordion.Item
        value={value}
        ref={itemRef}
        trackingId={generateTrackingId({
          component: 'follow-up-page',
          context: 'accordion-click',
          isDemoAccount,
        })}
      >
        <Accordion.Header css={styles.header}>
          <div css={styles.headerContainer}>
            <UserCard
              css={styles.userCard}
              firstName={followUpItem.contact.person?.firstName}
              lastName={followUpItem.contact.person?.lastName}
              preferredName={followUpItem.contact.person?.preferredName}
              locationId={followUpItem.contact.locationId}
              openPanelInTray={isDemoModal}
              openProfileOnClick={true}
              phoneNumber={callIntelUtils.getPhoneNumber(followUpItem.contact.phoneNumber)}
              rightElement={
                <CallIntelChip
                  chipVariants={chipVariants}
                  dataKey={followUpItem.contact.contactType ?? 'neutral'}
                  label={
                    followUpItem?.contact?.contactType
                      ? contactTypeLabelMap?.[followUpItem?.contact?.contactType] ?? ''
                      : ''
                  }
                />
              }
              showOnlyName
              userId={followUpItem.contact.person?.id}
            />

            {!isSmallMode && (
              <div css={styles.detailsWrapper}>
                <ContactActionsGroup followUpItem={followUpItem} customStyles={styles.detailsWrapperActions} />
                <div className='task-wrapper'>
                  <Icon color='subdued' name='task-small' />
                  <Text size='medium'>{t('{{ count }} Task', { count: totalTasks })}</Text>
                </div>

                <div className='calls-wrapper'>
                  <Icon color='subdued' name='phone-small' />
                  <Text size='medium'>
                    {t(`{{ totalCalls }} ${followUpItem.totalCalls > 1 ? 'Calls' : 'Call'}`, {
                      totalCalls: followUpItem.totalCalls,
                    })}
                  </Text>
                </div>

                <div className='time-stamp-wrapper'>
                  <Text color='subdued' size='medium'>
                    {t('Last Call')}
                  </Text>
                  <Text size='medium'>{dayjs(followUpItem.allCalls[0]?.startTime).format('MMM D, YYYY, h:mm A')}</Text>
                </div>
              </div>
            )}
          </div>
          {!isSmallMode && <ContactActionsGroup followUpItem={followUpItem} customStyles={styles.contactActions} />}
        </Accordion.Header>
        <Accordion.Body css={styles.body} ref={ref} data-testid={'follow-up-accordion-body'}>
          {displayedCalls.map((call) => {
            const tasks = call.id ? followUpItem.callTaskMapping[call.id] || [] : [];

            return (
              <div
                key={call.id}
                css={css`
                  margin-bottom: ${theme.spacing(2)};
                `}
                data-testid='follow-up-call-card'
              >
                <div css={[styles.callContainer, selectedCall?.id === call.id && styles.focusedCall]}>
                  <CallInfo
                    call={call}
                    isMultiLocation={!!isMultiLocation}
                    isSmallMode={isSmallMode}
                    locations={locations}
                  />
                  <IconButton
                    data-testid='call-insight-button'
                    css={styles.viewButtonWrapper}
                    labelPlacement='right'
                    onClick={() => handleOpenCallTakeaways(call)}
                    showLabelAlways
                    trackingId={generateTrackingId({
                      component: 'follow-up-page',
                      context: 'accordion-view-takeaway-btn-click',
                      isDemoAccount,
                    })}
                    label=''
                  >
                    <Icon className='view-btn-icon' name='document-filled' />
                  </IconButton>
                </div>

                {!isSmallMode ? (
                  <>
                    <div css={styles.taskTableHeader}>
                      <div className='data-cols'>
                        <Text size='medium' color='subdued'>
                          {t('Task')}
                        </Text>

                        <div css={styles.taskActionWrapper}>
                          <div className='status-column'>
                            <Text size='medium' color='subdued'>
                              {t('Status')}
                            </Text>
                          </div>
                          <div className='reason-column'>
                            <Text size='medium' color='subdued'>
                              {t('Follow-up Reason')}
                            </Text>
                          </div>
                        </div>
                      </div>

                      <div className='action-column' />
                    </div>

                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        css={styles.taskRow}
                        onClick={() => task.id && handleOpenTaskPanel({ callId: call.id ?? '', taskId: task.id })}
                        data-trackingid={generateTrackingId({
                          component: 'follow-up-page',
                          context: 'task-click',
                          isDemoAccount,
                        })}
                        data-testid='follow-up-task-row'
                      >
                        <div className='data-cols'>
                          <Text css={[designSystemStyles.multilineTruncate(2)]} size='medium'>
                            <Text as='span' weight='bold' size='medium'>
                              {task.title}
                            </Text>{' '}
                            {task.description}
                          </Text>
                        </div>

                        <div css={styles.taskActionWrapper}>
                          <div className='status-column'>
                            <TaskStatusSwitcher
                              defaultOptionValue={task.status}
                              onStatusChange={(newStatus) => {
                                if (task.id && call.id && task.status) {
                                  handleStatusChange({
                                    callId: call.id,
                                    taskId: task.id,
                                    newStatus,
                                    oldStatus: task.status,
                                  });
                                }
                              }}
                              trackingId={generateTrackingId({
                                component: 'follow-up-page',
                                context: 'task-status-switcher-btn-click',
                                isDemoAccount,
                              })}
                              value={task.status}
                            />
                          </div>

                          <div className='reason-column'>
                            <CallIntelChip
                              as='category'
                              chipVariants={chipVariants}
                              dataKey={task.type}
                              label={dataLabels.taskTypes?.[task.type] || task.type}
                              key={task.type}
                            />
                          </div>
                        </div>
                        <div className='action-column'>
                          <TaskMeatballMenu
                            isDemoAccount={isDemoAccount}
                            onDeleteTask={() =>
                              task.id && handleDeleteTaskConfirmation({ callId: call.id ?? '', taskId: task.id })
                            }
                            onViewCallTakeaways={() => handleOpenCallTakeaways(call)}
                            onViewTask={() =>
                              task.id && handleOpenTaskPanel({ callId: call.id ?? '', taskId: task.id })
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div css={styles.tasksMobileWrapper}>
                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        css={styles.taskMobileRow}
                        onClick={() => task.id && handleOpenTaskPanel({ callId: call.id ?? '', taskId: task.id })}
                        role='button'
                      >
                        <div className='task-content'>
                          <div className='task-item-wrapper'>
                            <Text css={[designSystemStyles.multilineTruncate(isSmallMode ? 3 : 4)]} size='medium'>
                              <Text as='span' weight='bold' size='medium'>
                                {task.title}
                              </Text>{' '}
                              {task.description}
                            </Text>
                          </div>

                          <div className='task-actions'>
                            <div className='task-item-wrapper'>
                              <TaskStatusSwitcher
                                defaultOptionValue={task.status}
                                onStatusChange={(newStatus) => {
                                  if (task.id && call.id && task.status) {
                                    handleStatusChange({
                                      callId: call.id,
                                      taskId: task.id,
                                      newStatus,
                                      oldStatus: task.status,
                                    });
                                  }
                                }}
                                trackingId={generateTrackingId({
                                  component: 'follow-up-page',
                                  context: 'task-status-switcher-btn-click',
                                  isDemoAccount,
                                })}
                                value={task.status}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {isLoadingTasks && (
                  <FollowUpLoader
                    count={
                      followUpItem.totalTasks <= MAX_EXPECTED_TASKS
                        ? followUpItem.totalTasks
                        : Math.floor(Math.random() * MAX_EXPECTED_TASKS) + 1
                    }
                    disabled={isLoadingTasks}
                    variant={!isSmallMode ? 'task' : 'taskMobile'}
                  />
                )}
              </div>
            );
          })}

          {hasMoreThanOnePage && (
            <div css={styles.loadMoreWrapper}>
              {canLoadMore ? (
                <Button variant='tertiary' onClick={handleLoadMore}>
                  {t('Load More Calls')}
                </Button>
              ) : (
                <Text size='small' color='subdued'>
                  {t('All calls loaded')}
                </Text>
              )}
            </div>
          )}
        </Accordion.Body>
        {selectedTaskConfig && <ConfirmationModal {...confirmationModalProps} />}
      </Accordion.Item>
    );
  }
);

FollowUpCard.displayName = 'FollowUpCard';

export const FollowUpsDashboard = ({
  isDrillDown,
  drillDownTaskType,
  searchQuery,
  sortOption,
  onSearchQueryChange,
  onSortOptionChange,
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isDemoAccount } = useCallIntelDemoFlags();
  const mainContentElement = useMemo(() => document.querySelector('#main-content') as HTMLElement, []);
  const [isSmallMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.xsmall.max,
    element: mainContentElement,
  });

  const [accordions, setAccordions] = useState<string[]>([]);
  const [pageConfig, setPageConfig] = useState<AnalyticsCommonTypes.PageConfig>({
    pageNumber: 1,
    pageSize: 10,
  });

  const [shouldLoadTasksMap, setShouldLoadTasksMap] = useState<Record<string, boolean>>({});
  const [isListsRendered, setIsListsRendered] = useState(false);
  const [isDataProcessing, setIsDataProcessing] = useState(true);

  const nodeMapRef = useRef<Map<Element, { contactId: string; index: number }>>(new Map());
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { followUps, totalPatients, taskStats, setFollowUps, setTotalPatients, setTaskStats } =
    useCallIntelFollowUpsShallowStore(
      'followUps',
      'totalPatients',
      'taskStats',
      'setFollowUps',
      'setTotalPatients',
      'setTaskStats'
    );
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { filters } = useActiveFilters();
  const { setIsDemoAccount } = useCallInsightController({
    source: 'callIntel',
  });

  const followupTaskTypeFilters: CallIntelTypes.FollowupTaskTypeFilters = useMemo(
    () => ({
      startDate: filters.startDate,
      endDate: filters.endDate,
      locations: filters.locations,
      officeUsers: filters.officeUsers,
      officeHours: filters.officeHours,
      contactTypes: filters.contactTypes,
      taskTypes: isDrillDown ? [drillDownTaskType] : filters.taskTypes ?? [],
      taskStatus: filters.taskStatus,
    }),
    [filters, isDrillDown, drillDownTaskType]
  );

  const { sortBy, sortType } = useMemo(() => CallIntelUtils.getFollowupSortValueByOption(sortOption), [sortOption]);

  const queryString = useMemo(
    () =>
      `calls-followups-${JSON.stringify(followupTaskTypeFilters)}-${searchQuery.query}-${pageConfig.pageSize}-${
        pageConfig.pageNumber
      }-${sortBy}-${sortType}-isDemoAccount-${isDemoAccount}`,
    [
      followupTaskTypeFilters,
      pageConfig.pageSize,
      pageConfig.pageNumber,
      searchQuery.query,
      sortBy,
      sortType,
      isDemoAccount,
    ]
  );

  const statsQueryString = useMemo(
    () =>
      `calls-followups-stats-${JSON.stringify(followupTaskTypeFilters)}-${pageConfig.pageSize}-${
        pageConfig.pageNumber
      }-${sortBy}-${sortType}-isDemoAccount-${isDemoAccount}`,
    [followupTaskTypeFilters, pageConfig.pageSize, pageConfig.pageNumber, sortBy, sortType, isDemoAccount]
  );

  const {
    data,
    hasPreviousPage,
    isFetching: isFollowUpLoading,
  } = useScopedInfiniteQuery({
    queryKey: queryKeys.callIntelligence(queryString),
    queryFn: () =>
      CallIntelligenceApi.getFollowUps({
        filters: followupTaskTypeFilters,
        pageConfig,
        searchQuery: searchQuery.value,
        sortBy,
        sortType,
      }),
    getNextPageParam: (data) => ({
      pageNumber: (data?.pageConfig?.pageNumber || 0) + 1,
      pageSize: data?.pageConfig?.pageSize || 10,
    }),
    getPreviousPageParam: (data) => ({
      pageNumber: (data?.pageConfig?.pageNumber || 2) - 1,
      pageSize: data?.pageConfig?.pageSize || 10,
    }),
    onError: () => {
      alert.error(t('Failed to fetch follow-ups'));
    },
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: statsRes } = useScopedQuery<CallIntelTypes.GetCallFollowupsStatsResponse | undefined, unknown>({
    queryKey: queryKeys.callIntelligence(statsQueryString),
    queryFn: () =>
      CallIntelligenceApi.getAllStats({
        filters: followupTaskTypeFilters,
      }),
    onError: () => {
      alert.error(t('Failed to fetch stats'));
    },
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const hasPrivateCalls = useMemo(
    () => !!data?.pages[pageConfig.pageNumber - 1]?.hasPrivateCalls,
    [data?.pages, pageConfig.pageNumber]
  );

  useEffect(() => {
    if (data) {
      const { allFollowUps, totalFollowUps } = processFollowUpsData(data);
      setFollowUps(allFollowUps);
      setTotalPatients(totalFollowUps);
      setAccordions(allFollowUps.map((followUp) => followUp.contact.contactId) ?? []);
      setIsDataProcessing(false);
    }
  }, [data]);

  useEffect(() => {
    if (statsRes?.stats) {
      setTaskStats({
        STATUS_TODO: statsRes.stats?.STATUS_TODO || 0,
        STATUS_IN_PROGRESS: statsRes.stats?.STATUS_IN_PROGRESS || 0,
        STATUS_COMPLETED: statsRes.stats?.STATUS_COMPLETED || 0,
      });
    }
  }, [statsRes?.stats]);

  // Intersection Observer for "lazy loading tasks"
  useEffect(() => {
    if (!followUps.length) return;

    // 2) Override PRELOAD_COUNT and rootMargin if pageSize is 100 for fast scrolling
    let effectivePreloadCount = DEFAULT_PRELOAD_COUNT;
    let effectiveRootMargin = '0px';
    if (pageConfig.pageSize === 100) {
      effectivePreloadCount = 5;
      effectiveRootMargin = '200px';
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // We'll batch newly visible items
        const newlyVisibleNodes: Array<{ contactId: string; index: number }> = [];

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Unobserve so we don't keep firing for the same element
            observer.unobserve(entry.target);

            // Find which contactId is associated with this DOM node
            const node = nodeMapRef.current.get(entry.target);
            if (!node) return;

            const { contactId, index } = node;

            // If it's already marked, skip
            if (shouldLoadTasksMap[contactId]) {
              return;
            }

            // We'll collect it to handle in one setState call
            newlyVisibleNodes.push({ contactId, index });
          }
        });

        // 3) After processing all intersecting entries, do ONE state update
        if (newlyVisibleNodes.length > 0) {
          setShouldLoadTasksMap((prev) => {
            const updated = { ...prev };

            // For each newly visible contactId, plus up to PRELOAD_COUNT items
            newlyVisibleNodes.forEach(({ index }) => {
              for (let i = index; i < index + effectivePreloadCount + 1 && i < followUps.length; i++) {
                const nextId = followUps[i].contact.contactId;
                updated[nextId] = true;
              }
            });

            return updated;
          });
        }
      },
      {
        root: null,
        rootMargin: effectiveRootMargin,
        threshold: 0.0, // triggers as soon as item enters the viewport
      }
    );

    if (isListsRendered) {
      followUps.forEach((followUp) => {
        const contactId = followUp.contact.contactId;

        const node = [...nodeMapRef.current.keys()].find((el) => {
          const data = nodeMapRef.current.get(el);
          return data?.contactId === contactId;
        });
        if (node) observer.observe(node);
      });
    }

    return () => observer.disconnect();
  }, [followUps, isListsRendered, isDemoAccount, pageConfig.pageSize]);

  useEffect(() => {
    setIsDemoAccount(!!isDemoAccount);
  }, [isDemoAccount, setIsDemoAccount]);

  const handleAccordionChange = useCallback((value: string[]) => {
    setAccordions(value);
  }, []);

  useEffect(() => {
    if (isFollowUpLoading) {
      handleReset();
    }
  }, [isFollowUpLoading]);

  const handleReset = () => {
    setIsDataProcessing(true);
    setAccordions([]);
    setShouldLoadTasksMap({});
    setIsListsRendered(false);
    setFollowUps([]);
    setTaskStats({
      STATUS_TODO: 0,
      STATUS_IN_PROGRESS: 0,
      STATUS_COMPLETED: 0,
    });

    nodeMapRef.current.clear();
    wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const taskStatusFilters = useMemo(() => filters.taskStatus || [], [filters.taskStatus]);

  useEffect(() => {
    // Reset to first page on filter change
    setPageConfig((current) => ({ ...current, pageNumber: 1 }));
  }, [filters]);

  return (
    <div ref={wrapperRef} css={styles.wrapper}>
      {isFollowUpLoading ? (
        <FollowUpLoader variant='stats' />
      ) : (
        <FollowUpStats
          isSmallMode={isSmallMode}
          totalPatients={totalPatients}
          taskStats={taskStats}
          taskStatusFilters={taskStatusFilters}
        />
      )}
      <div css={styles.followUpWrapper}>
        {/* Small mode search and sort will be rendered by filter component in parent */}
        {!isSmallMode && (
          <div css={styles.filterContainer}>
            <CallIntelSortFilter
              selectedSortOption={sortOption}
              onSortChange={onSortOptionChange}
              trackingId={trackingIds.callIntel.followUpSortFilter}
            />
            <SearchFilter
              minSearchLength={MIN_SEARCH_LENGTH}
              onChange={onSearchQueryChange}
              value={searchQuery.value}
              trackingId={trackingIds.callIntel.followUpPageSearch}
              placeholder={t('Search Name or Phone Number')}
              css={styles.searchInput}
            />
          </div>
        )}

        {hasPrivateCalls && <PrivateCommsBanner source='follow-up-dashboard' />}

        {isFollowUpLoading || isDataProcessing ? (
          <FollowUpLoader count={pageConfig.pageSize} variant='followUp' />
        ) : (
          <>
            {!followUps.length ? (
              <EmptyState
                label={t(`No {{taskTypeLabel}} Follow-Ups`, {
                  taskTypeLabel: dataLabels?.taskTypes?.[drillDownTaskType] ?? drillDownTaskType,
                })}
              />
            ) : (
              <Accordion
                css={styles.accordion}
                controlledValue={accordions}
                shouldRenderBody
                onChange={handleAccordionChange}
                isMulti
              >
                {followUps.map((followUp, idx) => {
                  const id = followUp.contact.contactId;
                  return (
                    <FollowUpCard
                      drillDownTaskType={drillDownTaskType}
                      followUpItem={followUp}
                      isDrillDown={isDrillDown}
                      isSmallMode={isSmallMode}
                      key={id}
                      ref={(node) => {
                        if (node) {
                          nodeMapRef.current.set(node, { contactId: id, index: idx });
                        } else {
                          nodeMapRef.current.forEach((_, key) => {
                            if (key === node) nodeMapRef.current.delete(key);
                          });
                        }

                        if (idx === followUps.length - 1) {
                          setIsListsRendered(true);
                        }
                      }}
                      shouldLoadTasks={!!shouldLoadTasksMap[id]}
                      taskStatusFilters={taskStatusFilters}
                      value={id}
                    />
                  );
                })}
              </Accordion>
            )}
          </>
        )}
      </div>

      <Pagination
        handleChange={(action: 'prev' | 'next'): void => {
          if (action === 'next') {
            setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber + 1 });
          } else {
            setPageConfig({ ...pageConfig, pageNumber: pageConfig.pageNumber - 1 });
          }
        }}
        hasNext={data?.pages[pageConfig.pageNumber - 1]?.hasNextPage}
        hasPrevious={hasPreviousPage}
        onNumRowsChange={(num) => {
          setPageConfig({ pageNumber: 1, pageSize: num });
        }}
        page={pageConfig.pageNumber}
        rowsPerPageOptions={[10, 25, 50, 100]}
        globalTrackingId={generateTrackingId({
          component: 'follow-up-page',
          isDemoAccount,
        })}
      />
    </div>
  );
};

const styles = {
  accordion: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};

    > div {
      box-shadow: none;
      border: 1px solid ${theme.colors.neutral20};
      margin-bottom: 0px;
    }

    > div h3 button section div svg {
      fill: ${theme.colors.neutral70};
    }

    div[data-active='true'] h3 button {
      background-color: ${theme.colors.neutral5};
      border-bottom: 1px solid ${theme.colors.neutral10};

      section div svg {
        fill: ${theme.colors.neutral90};
      }
    }
  `,

  body: css`
    padding: ${theme.spacing(2)};
  `,

  detailsWrapper: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    flex-wrap: wrap;
    flex: 1;
    row-gap: ${theme.spacing(0.5)};

    .task-wrapper {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
      width: 88px;
      p {
        white-space: nowrap;
      }
    }

    .calls-wrapper {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
      width: 88px;
      p {
        white-space: nowrap;
      }
    }

    .time-stamp-wrapper {
      display: flex;
      gap: ${theme.spacing(0.5)};

      p {
        white-space: nowrap;
      }
    }
  `,

  userCard: css`
    width: 304px;
    min-width: 304px;

    @media screen and (max-width: 620px) {
      width: auto;
      min-width: auto;
    }

    > div {
      cursor: pointer !important;
      flex: 1;
      > figure {
        height: ${theme.spacing(4)};
        width: ${theme.spacing(4)};
        min-height: ${theme.spacing(4)};
        min-width: ${theme.spacing(4)};
        font-size: ${theme.font.size.small};
      }

      > div {
        width: auto !important;
      }

      .name-wrapper {
        flex-wrap: wrap;
        row-gap: ${theme.spacing(0.5)};
        > p {
          font-size: ${theme.font.size.h3};
          line-height: ${theme.spacing(3)};
          text-align: left;

          @container (max-width: ${breakpoints.xsmall.max}px) {
            font-size: ${theme.font.size.medium};
          }
          span {
            font-size: ${theme.font.size.h3};

            @container (max-width: ${breakpoints.xsmall.max}px) {
              font-size: ${theme.font.size.medium};
            }
          }
        }

        > div > span {
          font-size: ${theme.font.size.small};
        }
      }
    }
  `,

  emptyStateWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `,

  emptyStateText: css`
    color: ${theme.colors.neutral50};
  `,

  followUpLoaderWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};

    .row {
      border-radius: ${theme.spacing(1)};
    }
  `,

  filterContainer: css`
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
    flex-wrap: wrap;
  `,

  header: css`
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};

    section {
      padding: ${theme.spacing(0.5)};
      border: none;
      marging-right: ${theme.spacing(0)};
      width: ${theme.spacing(4)};
      height: ${theme.spacing(4)};
    }
    .header-container {
      display: flex;
      padding: 0px;
      gap: ${theme.spacing(4)};
      justify-content: space-between;
      align-items: center;
    }
  `,

  searchInput: css`
    width: 300px;
    @media screen and (max-width: 475px) {
      width: 285px;
      input {
        font-size: ${theme.font.size.large};
      }
    }
  `,

  statsWrapper: css`
    width: 100%;
    display: flex;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(3)};
    border: ${theme.spacing(0.125)} solid ${theme.font.colors.disabled};
    border-radius: ${theme.borderRadius.medium};
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    @container main-content (max-width: ${breakpoints.xsmall.max}px) {
      justify-content: space-between;
    }

    @container main-content (max-width: 340px) {
      justify-content: space-around;
    }

    .info-wrapper {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1)};
      align-items: center;

      .value {
        font-size: ${theme.spacing(4.5)};
        line-height: ${theme.spacing(5)};

        @container main-content (max-width: ${breakpoints.xsmall.max}px) {
          font-size: ${theme.spacing(3)};
        }
      }
    }
  `,

  taskActionWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,

  taskRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing(2)};
    border-bottom: 1px solid ${theme.colors.neutral20};
    gap: ${theme.spacing(1)};

    .data-cols {
      display: flex;
      flex: 1;
      justify-content: space-between;
      align-items: center;
    }

    .status-column {
      display: flex;
      justify-content: flex-start;
      width: 130px;
    }

    .reason-column {
      display: flex;
      justify-content: flex-start;
      width: 150px;
      > div {
        justify-content: flex-start;
      }
    }

    .action-column {
      display: flex;
      justify-content: flex-end;
      width: 32px;
    }

    :hover {
      background-color: ${theme.colors.neutral5};
      cursor: pointer;
    }
  `,

  taskMobileRow: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(0, 2, 2, 2)};
    cursor: pointer;
    @container main-content (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: row;
      padding: ${theme.spacing(0)};
    }

    .meatball-menu-wrapper {
      display: flex;
      padding: ${theme.spacing(1)};
      background-color: ${theme.colors.neutral5};
      justify-content: flex-end;
      border-radius: ${theme.borderRadius.small};
    }

    .task-content {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1)};
      padding: ${theme.spacing(2)};
      @container main-content (max-width: ${breakpoints.xsmall.max}px) {
        flex-direction: row;
        align-items: flex-start;
      }
    }

    .task-item-wrapper {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1)};
    }

    .task-actions {
      display: flex;
      gap: ${theme.spacing(1)};
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
    }
  `,

  viewButtonWrapper: css`
    color: ${theme.colors.text.default};
    flex: 0 0 auto;
    font-weight: ${theme.font.weight.bold};
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.5)};
    min-height: ${theme.spacing(4)};
    min-width: ${theme.spacing(4)};
    height: ${theme.spacing(4)};
    border: none;
  `,

  wrapper: css`
    scroll-margin-top: 600px;
  `,
  followUpWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    margin: ${theme.spacing(5, 0)};
  `,

  callWrapper: css`
    display: flex;
    gap: ${theme.spacing(4)};
    row-gap: ${theme.spacing(0.5)};
    flex-wrap: wrap;

    @container main-content (max-width: ${breakpoints.xsmall.max}px) {
      gap: ${theme.spacing(1)};
    }

    .office-user-wrapper {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: ${theme.spacing(1)};

      p {
        white-space: nowrap;
      }
    }

    .time-stamp-wrapper {
      display: flex;
      gap: ${theme.spacing(1)};
      justify-content: flex-start;
      align-items: center;

      p {
        white-space: nowrap;
      }
    }

    .location-wrapper {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: ${theme.spacing(1)};
    }
  `,
  actionsWrapper: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;

    button {
      border: none !important;
      background-color: inherit !important;
      width: ${theme.spacing(4)};
      height: ${theme.spacing(4)};
      min-width: ${theme.spacing(4)};
      min-height: ${theme.spacing(4)};
      svg {
        width: ${theme.spacing(3)};
        height: ${theme.spacing(3)};
      }
    }

    button:hover {
      color: inherit !important;
    }
  `,
  headerContainer: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: ${theme.spacing(1)};
  `,

  taskTableHeader: css`
    display: flex;
    padding: ${theme.spacing(2)};
    align-items: center;
    gap: ${theme.spacing(1)};
    border-bottom: 1px solid ${theme.colors.neutral20};

    .data-cols {
      display: flex;
      flex: 1;
      justify-content: space-between;
      align-items: center;
    }

    .status-column {
      display: flex;
      justify-content: flex-start;
      width: 130px;
    }

    .reason-column {
      display: flex;
      justify-content: flex-start;
      width: 150px;
    }

    .action-column {
      display: flex;
      justify-content: flex-end;
      width: 32px;
    }
  `,

  focusedCall: css`
    border: 1px solid ${theme.colors.primary50};
  `,
  detailsWrapperActions: css`
    display: none;
    @media screen and (max-width: ${breakpoints.small.max}px) {
      display: flex;
      flex: 1 1 100%;
    }
  `,
  detailsWrapperActionsMobile: css`
    display: flex;
    flex: 1 1 100%;
  `,
  contactActions: css`
    display: flex;
    @media screen and (max-width: ${breakpoints.small.max}px) {
      display: none;
    }
  `,
  contactActionsMobile: css`
    display: none;
  `,
  callContainer: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing(1, 2)};
    gap: ${theme.spacing(2)};
  `,

  loadMoreWrapper: css`
    text-align: center;
  `,
  meatBallBtnWrapper: css`
    padding: ${theme.spacing(0.5)};
    min-height: ${theme.spacing(4)};
    min-width: ${theme.spacing(4)};
    height: ${theme.spacing(4)};
    width: ${theme.spacing(4)};
  `,
  tasksMobileWrapper: css`
    > div:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }
  `,
  taskMobileLoadingWrapper: css`
    display: flex;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
  `,
  officeUserNameText: css`
    > span {
      font-size: ${theme.fontSize(14)};
    }
  `,
};
